/**
 * React Query hook for LLM providers
 *
 * Provides data fetching and mutations for LLM provider configurations
 * with automatic caching and cache invalidation.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { llmSettingsApi } from '../services/llmSettingsApi';
import type {
  SavedProvider,
  LLMProviderRequest,
  LLMProviderConfig,
  SaveProviderResponse,
  ProviderActionResponse,
} from '../types/llmSettings';

// Query key for LLM providers
export const LLM_PROVIDERS_QUERY_KEY = ['llmProviders'] as const;

/**
 * Update provider request
 */
export interface UpdateProviderRequest {
  providerId: number;
  config: Partial<LLMProviderConfig>;
  makeActive?: boolean;
}

/**
 * Hook return type for useLLMProviders
 */
export interface UseLLMProvidersResult {
  /** List of saved providers */
  providers: SavedProvider[] | undefined;
  /** Loading state for initial fetch */
  isLoading: boolean;
  /** Fetching state (true during refetch) */
  isFetching: boolean;
  /** Error from fetching providers */
  error: Error | null;
  /** Function to save a new provider */
  saveProvider: (request: LLMProviderRequest) => Promise<SaveProviderResponse>;
  /** Loading state for save mutation */
  isSaving: boolean;
  /** Error from save mutation */
  saveError: Error | null;
  /** Function to update an existing provider */
  updateProvider: (request: UpdateProviderRequest) => Promise<SaveProviderResponse>;
  /** Loading state for update mutation */
  isUpdating: boolean;
  /** Error from update mutation */
  updateError: Error | null;
  /** Function to delete a provider */
  deleteProvider: (providerId: number) => Promise<ProviderActionResponse>;
  /** Loading state for delete mutation */
  isDeleting: boolean;
  /** Error from delete mutation */
  deleteError: Error | null;
  /** Function to activate a provider */
  activateProvider: (providerId: number) => Promise<ProviderActionResponse>;
  /** Loading state for activate mutation */
  isActivating: boolean;
  /** Error from activate mutation */
  activateError: Error | null;
  /** Refetch providers */
  refetch: () => void;
}

/**
 * React Query hook for managing LLM providers
 *
 * @returns LLM providers data, loading states, and mutation functions
 *
 * @example
 * ```tsx
 * const { providers, isLoading, saveProvider, deleteProvider } = useLLMProviders();
 *
 * if (isLoading) return <Spinner />;
 *
 * return (
 *   <ProviderList
 *     providers={providers}
 *     onSave={saveProvider}
 *     onDelete={deleteProvider}
 *   />
 * );
 * ```
 */
export function useLLMProviders(): UseLLMProvidersResult {
  const queryClient = useQueryClient();

  // Query for fetching LLM providers using getStatus (includes detailed provider info)
  const {
    data: providers,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useQuery({
    queryKey: LLM_PROVIDERS_QUERY_KEY,
    queryFn: async () => {
      const statusResponse = await llmSettingsApi.getStatus();
      if (statusResponse.detailedProviders) {
        return statusResponse.detailedProviders.map((provider) => ({
          id: provider.id,
          provider: provider.provider,
          displayName: provider.displayName,
          isActive: provider.isActive,
          timeoutSeconds: provider.timeoutSeconds,
          testStatus: provider.testStatus,
          lastTested: provider.lastTested,
          createdAt: provider.createdAt || new Date().toISOString(),
        }));
      }
      return [];
    },
  });

  // Mutation for saving a new provider
  const {
    mutateAsync: saveProvider,
    isPending: isSaving,
    error: saveError,
  } = useMutation({
    mutationFn: (request: LLMProviderRequest) => llmSettingsApi.saveProvider(request),
    onSuccess: () => {
      // Invalidate and refetch providers list
      queryClient.invalidateQueries({ queryKey: LLM_PROVIDERS_QUERY_KEY });
    },
  });

  // Mutation for updating an existing provider
  const {
    mutateAsync: updateProviderMutation,
    isPending: isUpdating,
    error: updateError,
  } = useMutation({
    mutationFn: (request: UpdateProviderRequest) =>
      llmSettingsApi.updateProvider(request.providerId, request.config, request.makeActive ?? false),
    onSuccess: () => {
      // Invalidate and refetch providers list
      queryClient.invalidateQueries({ queryKey: LLM_PROVIDERS_QUERY_KEY });
    },
  });

  // Mutation for deleting a provider
  const {
    mutateAsync: deleteProvider,
    isPending: isDeleting,
    error: deleteError,
  } = useMutation({
    mutationFn: (providerId: number) => llmSettingsApi.deleteProvider(providerId),
    onSuccess: () => {
      // Invalidate and refetch providers list
      queryClient.invalidateQueries({ queryKey: LLM_PROVIDERS_QUERY_KEY });
    },
  });

  // Mutation for activating a provider
  const {
    mutateAsync: activateProvider,
    isPending: isActivating,
    error: activateError,
  } = useMutation({
    mutationFn: (providerId: number) => llmSettingsApi.activateProvider(providerId),
    onSuccess: () => {
      // Invalidate and refetch providers list
      queryClient.invalidateQueries({ queryKey: LLM_PROVIDERS_QUERY_KEY });
    },
  });

  return {
    providers,
    isLoading,
    isFetching,
    error: error as Error | null,
    saveProvider,
    isSaving,
    saveError: saveError as Error | null,
    updateProvider: updateProviderMutation,
    isUpdating,
    updateError: updateError as Error | null,
    deleteProvider,
    isDeleting,
    deleteError: deleteError as Error | null,
    activateProvider,
    isActivating,
    activateError: activateError as Error | null,
    refetch,
  };
}
